import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Medal, Flame, Star, ChevronDown, Info, Zap } from 'lucide-react';
import RetroSelect from '../RetroSelect';

const LeaderboardSection = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [selectedQuest, setSelectedQuest] = useState('handwritten');

  const quests = [
    { id: 'handwritten', label: 'Handwritten Notes & Problem Solving' },
    { id: 'laundry', label: 'Folding Clothes' },
    { id: 'homeops', label: 'Household Tasks' },
    { id: 'tools', label: 'Tool Usage' },
  ];

  const generalLeaderboard = [
    { rank: 1, name: 'DiskarteDroid', score: 99, submissions: 40, streak: 12, reward: '₱15,000' },
    { rank: 2, name: 'LaundryLegend', score: 97, submissions: 38, streak: 10, reward: '₱12,000' },
    { rank: 3, name: 'BahayOpsPro', score: 95, submissions: 35, streak: 8, reward: '₱10,000' },
    { rank: 4, name: 'PisaiSolver', score: 93, submissions: 32, streak: 9, reward: '₱8,500' },
    { rank: 5, name: 'ToolTimeKing', score: 91, submissions: 30, streak: 7, reward: '₱7,200' },
    { rank: 6, name: 'DataHustle88', score: 89, submissions: 28, streak: 6, reward: '₱6,000' },
    { rank: 7, name: 'QuestMaster_PH', score: 87, submissions: 25, streak: 5, reward: '₱5,000' },
    { rank: 8, name: 'SigawNgLaba', score: 85, submissions: 22, streak: 4, reward: '₱4,200' },
    { rank: 9, name: 'KusineroBot', score: 83, submissions: 18, streak: 6, reward: '₱3,500' },
    { rank: 10, name: 'GawainGrinder', score: 81, submissions: 15, streak: 3, reward: '₱2,800' },
    { rank: 11, name: 'TaskTitanPH', score: 79, submissions: 10, streak: 2, reward: '₱2,200' },
    { rank: 12, name: 'NoteNinja99', score: 78, submissions: 8, streak: 1, reward: '₱1,800' },
  ];

  const questLeaderboards = {
    handwritten: [
      { rank: 1, name: 'PisaiSolver', score: 98, submissions: 18, streak: 8, reward: '₱5,000' },
      { rank: 2, name: 'NoteNinja99', score: 95, submissions: 15, streak: 6, reward: '₱4,000' },
      { rank: 3, name: 'MathMaestro', score: 92, submissions: 12, streak: 5, reward: '₱3,000' },
      { rank: 4, name: 'DiagramDon', score: 88, submissions: 10, streak: 4, reward: '₱2,500' },
      { rank: 5, name: 'SolverSupreme', score: 85, submissions: 8, streak: 3, reward: '₱2,000' },
    ],
    laundry: [
      { rank: 1, name: 'LaundryLegend', score: 99, submissions: 22, streak: 10, reward: '₱5,500' },
      { rank: 2, name: 'SigawNgLaba', score: 94, submissions: 18, streak: 7, reward: '₱4,200' },
      { rank: 3, name: 'FoldMaster', score: 90, submissions: 14, streak: 5, reward: '₱3,200' },
      { rank: 4, name: 'PlantsaPro', score: 86, submissions: 10, streak: 4, reward: '₱2,600' },
      { rank: 5, name: 'LabaLord', score: 82, submissions: 7, streak: 2, reward: '₱2,000' },
    ],
    homeops: [
      { rank: 1, name: 'BahayOpsPro', score: 97, submissions: 25, streak: 9, reward: '₱5,800' },
      { rank: 2, name: 'KusineroBot', score: 93, submissions: 20, streak: 7, reward: '₱4,500' },
      { rank: 3, name: 'CleanQueen', score: 89, submissions: 16, streak: 5, reward: '₱3,400' },
      { rank: 4, name: 'GroceryGuru', score: 85, submissions: 12, streak: 4, reward: '₱2,700' },
      { rank: 5, name: 'DishDynamo', score: 81, submissions: 8, streak: 3, reward: '₱2,100' },
    ],
    tools: [
      { rank: 1, name: 'ToolTimeKing', score: 96, submissions: 20, streak: 8, reward: '₱5,200' },
      { rank: 2, name: 'DiskarteDroid', score: 92, submissions: 16, streak: 6, reward: '₱4,000' },
      { rank: 3, name: 'BuilderBoss', score: 88, submissions: 12, streak: 5, reward: '₱3,000' },
      { rank: 4, name: 'SafetyFirst', score: 84, submissions: 9, streak: 4, reward: '₱2,400' },
      { rank: 5, name: 'HammerHero', score: 80, submissions: 6, streak: 2, reward: '₱1,900' },
    ],
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <div className="w-8 h-8 rounded flex items-center justify-center rank-1"><Crown className="w-5 h-5 text-dark-border" /></div>;
    if (rank === 2) return <div className="w-8 h-8 rounded flex items-center justify-center rank-2"><Medal className="w-5 h-5 text-dark-border" /></div>;
    if (rank === 3) return <div className="w-8 h-8 rounded flex items-center justify-center rank-3"><Medal className="w-5 h-5 text-dark-border" /></div>;
    return <div className="w-8 h-8 rounded flex items-center justify-center bg-panel-purple border border-dark-border font-pixel text-xs text-foreground">{rank}</div>;
  };

  const currentData = activeTab === 'general' ? generalLeaderboard : questLeaderboards[selectedQuest];

  return (
    <section id="leaderboard" className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-16 h-1 bg-neon-pink" />
            <Star className="w-8 h-8 text-trophy-gold" />
            <div className="w-16 h-1 bg-neon-pink" />
          </div>
          <h2 className="font-pixel text-xl sm:text-2xl md:text-3xl text-trophy-gold text-glow-gold mb-4">
            LEADERBOARD
          </h2>
          <p className="font-terminal text-xl text-lavender">
            Top Side Questers earning cash rewards
          </p>
        </motion.div>

        {/* Leaderboard Panel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg overflow-hidden"
        >
          {/* Tab Switcher */}
          <div className="flex border-b-4 border-dark-border">
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 font-pixel text-[10px] sm:text-xs py-4 px-4 transition-all ${
                activeTab === 'general'
                  ? 'bg-neon-pink text-dark-border'
                  : 'bg-panel-purple text-lavender hover:bg-neon-pink/20'
              }`}
            >
              <Star className="inline-block w-4 h-4 mr-2" />
              GENERAL LEADERBOARD
            </button>
            <button
              onClick={() => setActiveTab('quest')}
              className={`flex-1 font-pixel text-[10px] sm:text-xs py-4 px-4 transition-all ${
                activeTab === 'quest'
                  ? 'bg-neon-pink text-dark-border'
                  : 'bg-panel-purple text-lavender hover:bg-neon-pink/20'
              }`}
            >
              <Zap className="inline-block w-4 h-4 mr-2" />
              PER-QUEST LEADERBOARD
            </button>
          </div>

          {/* Quest Dropdown (for quest tab) */}
          <AnimatePresence>
            {activeTab === 'quest' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b-4 border-dark-border"
              >
                <div className="p-4 bg-deep-purple/50 relative z-20">
                  <label className="font-terminal text-lg text-lavender block mb-2">SELECT SIDE QUEST:</label>
                  <RetroSelect
                    value={selectedQuest}
                    onChange={(e) => setSelectedQuest(e.target.value)}
                    placeholder="Select a quest..."
                    options={quests.map(q => ({ value: q.id, label: q.label }))}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Leaderboard Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-deep-purple/50 border-b-2 border-dark-border">
                  <th className="font-pixel text-[8px] sm:text-[10px] text-neon-pink py-4 px-3 text-left">RANK</th>
                  <th className="font-pixel text-[8px] sm:text-[10px] text-neon-pink py-4 px-3 text-left">SIDE QUESTER</th>
                  <th className="font-pixel text-[8px] sm:text-[10px] text-neon-pink py-4 px-3 text-center">SCORE</th>
                  <th className="font-pixel text-[8px] sm:text-[10px] text-neon-pink py-4 px-3 text-center hidden sm:table-cell">ACCEPTED</th>
                  <th className="font-pixel text-[8px] sm:text-[10px] text-neon-pink py-4 px-3 text-center hidden md:table-cell">STREAK</th>
                  <th className="font-pixel text-[8px] sm:text-[10px] text-trophy-gold py-4 px-3 text-right">EST. REWARD</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {currentData.map((player, index) => (
                    <motion.tr
                      key={`${activeTab}-${selectedQuest}-${player.rank}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.05 }}
                      className={`leaderboard-row border-b border-panel-purple ${
                        player.rank <= 3 ? 'bg-trophy-gold/5' : ''
                      }`}
                    >
                      <td className="py-4 px-3">
                        {getRankBadge(player.rank)}
                      </td>
                      <td className="py-4 px-3">
                        <span className="font-terminal text-xl text-foreground">{player.name}</span>
                      </td>
                      <td className="py-4 px-3 text-center">
                        <span className={`font-pixel text-sm ${player.score >= 90 ? 'text-success-green' : 'text-foreground'}`}>
                          {player.score}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-center hidden sm:table-cell">
                        <span className="font-terminal text-xl text-lavender">{player.submissions}</span>
                      </td>
                      <td className="py-4 px-3 text-center hidden md:table-cell">
                        <span className="inline-flex items-center gap-1 font-terminal text-xl text-warning-orange">
                          <Flame className="w-4 h-4" />
                          {player.streak}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-right">
                        <span className="font-pixel text-xs sm:text-sm text-trophy-gold">{player.reward}</span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Note */}
          <div className="p-4 bg-deep-purple/50 border-t-2 border-dark-border">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-neon-pink flex-shrink-0 mt-0.5" />
              <p className="font-terminal text-base sm:text-lg text-lavender">
                <span className="text-neon-pink">NOTE:</span> Only approved and accepted submissions count toward leaderboards and payouts. 
                Scoring factors include: <span className="text-foreground">clarity, completeness, instruction accuracy, usefulness for AI/robotics, and edge-case value.</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeaderboardSection;
